import React, { useState, useEffect } from "react";


const API_URL = process.env.REACT_APP_BACKEND_URL || "https://tspm-api.abrandfantastic.workers.dev";

// Mini page map: shows which zone on the RAD homepage is being edited
function PageMap({ active }) {
  const zones = [
    { id: "hero", label: "Hero" },
    { id: "featured", label: "Featured" },
    { id: "editorial", label: "Editorial" },
    { id: "brand", label: "Brand" },
    { id: "footer", label: "Footer" },
  ];
  return (
    <div className="flex items-stretch gap-px bg-gray-200 border border-gray-200 mb-3 text-[10px] overflow-hidden">
      {/* Nav bar indicator */}
      <div className="bg-gray-100 px-2 py-1 text-gray-400 flex items-center font-medium tracking-wide shrink-0">
        rad-one.pages.dev
      </div>
      <div className="flex flex-1 gap-px">
        {zones.map(z => (
          <a
            key={z.id}
            href={`https://rad-one.pages.dev/#${z.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex-1 px-1 py-1 text-center font-medium tracking-wide transition-colors ${
              z.id === active
                ? "bg-black text-white"
                : "bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
            }`}
          >
            {z.label}
          </a>
        ))}
      </div>
    </div>
  );
}

const SECTIONS = [
  {
    id: "hero",
    title: "Hero Banner",
    fields: [
      { key: "hero_subtitle", label: "Eyebrow", hint: 'Small text above the title — e.g. "Women\'s Streetwear"' },
      { key: "hero_title", label: "Title", hint: 'Main bold headline — e.g. "BREAK THE RULES"' },
      { key: "hero_description", label: "Description", hint: "Tagline below the title" },
      { key: "hero_cta", label: "Button Text", hint: 'e.g. "SHOP NOW"' },
    ],
    Preview: ({ draft }) => (
      <div className="bg-black text-white p-6 h-full flex flex-col justify-end min-h-[200px]">
        <p className="text-xs tracking-widest uppercase opacity-60 mb-2">{draft.hero_subtitle || "—"}</p>
        <p className="text-2xl font-black uppercase tracking-tight mb-2">{draft.hero_title || "—"}</p>
        <p className="text-sm opacity-70 mb-4">{draft.hero_description || "—"}</p>
        <span className="text-xs uppercase tracking-widest border border-white px-4 py-2 w-fit">{draft.hero_cta || "—"}</span>
      </div>
    ),
  },
  {
    id: "featured",
    title: "Featured Section",
    fields: [
      { key: "featured_eyebrow", label: "Eyebrow", hint: 'Small label above the heading — e.g. "What\'s New"' },
      { key: "featured_title", label: "Title", hint: 'Section heading — e.g. "Featured"' },
    ],
    Preview: ({ draft }) => (
      <div className="bg-white border border-gray-200 p-6 h-full min-h-[120px]">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-gray-400 mb-1">{draft.featured_eyebrow || "—"}</p>
        <p className="text-2xl font-black uppercase tracking-tight">{draft.featured_title || "—"}</p>
      </div>
    ),
  },
  {
    id: "editorial",
    title: "Editorial Grid",
    fields: [
      { key: "editorial_1_eyebrow", label: "Panel 1 — Eyebrow", hint: 'Large left panel — e.g. "Collection 01"' },
      { key: "editorial_1_title", label: "Panel 1 — Title", hint: 'e.g. "Edge"' },
      { key: "editorial_1_cta", label: "Panel 1 — Button", hint: 'e.g. "Explore"' },
      { key: "editorial_2_eyebrow", label: "Panel 2 — Eyebrow", hint: 'Top right panel — e.g. "Collection 02"' },
      { key: "editorial_2_title", label: "Panel 2 — Title", hint: 'e.g. "Street"' },
      { key: "editorial_2_cta", label: "Panel 2 — Link", hint: 'e.g. "View"' },
      { key: "editorial_3_eyebrow", label: "Panel 3 — Eyebrow", hint: 'Bottom right panel — e.g. "Lookbook"' },
      { key: "editorial_3_title", label: "Panel 3 — Title", hint: 'e.g. "Studio"' },
      { key: "editorial_3_cta", label: "Panel 3 — Link", hint: 'e.g. "View"' },
    ],
    Preview: ({ draft }) => (
      <div className="bg-gray-900 text-white p-4 h-full min-h-[220px] flex flex-col gap-2">
        <div className="flex-[2] bg-black/40 p-3 flex flex-col justify-end">
          <p className="text-[10px] tracking-widest uppercase opacity-60">{draft.editorial_1_eyebrow || "—"}</p>
          <p className="text-lg font-black uppercase tracking-tight">{draft.editorial_1_title || "—"}</p>
          <span className="text-[10px] uppercase tracking-widest border border-white px-2 py-1 w-fit mt-1">{draft.editorial_1_cta || "—"}</span>
        </div>
        <div className="flex gap-2 flex-1">
          <div className="flex-1 bg-black/40 p-3 flex flex-col justify-end">
            <p className="text-[10px] tracking-widest uppercase opacity-60">{draft.editorial_2_eyebrow || "—"}</p>
            <p className="text-sm font-black uppercase tracking-tight">{draft.editorial_2_title || "—"}</p>
            <p className="text-[10px] uppercase tracking-widest opacity-70 mt-1">{draft.editorial_2_cta || "—"} →</p>
          </div>
          <div className="flex-1 bg-black/40 p-3 flex flex-col justify-end">
            <p className="text-[10px] tracking-widest uppercase opacity-60">{draft.editorial_3_eyebrow || "—"}</p>
            <p className="text-sm font-black uppercase tracking-tight">{draft.editorial_3_title || "—"}</p>
            <p className="text-[10px] uppercase tracking-widest opacity-70 mt-1">{draft.editorial_3_cta || "—"} →</p>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: "brand",
    title: "Brand Statement",
    fields: [
      { key: "brand_eyebrow", label: "Eyebrow", hint: 'e.g. "Our Philosophy"' },
      { key: "brand_title_1", label: "Line 1", hint: 'e.g. "FASHION WITHOUT APOLOGY."' },
      { key: "brand_title_2", label: "Line 2 (grey)", hint: 'e.g. "RULES WITHOUT LIMITS."' },
      { key: "brand_cta", label: "Button Text", hint: 'e.g. "Our Story"' },
    ],
    Preview: ({ draft }) => (
      <div className="bg-white border border-gray-200 p-6 text-center h-full min-h-[160px] flex flex-col items-center justify-center">
        <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-gray-400 mb-3">{draft.brand_eyebrow || "—"}</p>
        <p className="text-xl font-black uppercase tracking-tight leading-tight">
          {draft.brand_title_1 || "—"}
        </p>
        <p className="text-xl font-black uppercase tracking-tight leading-tight text-gray-300 mb-4">
          {draft.brand_title_2 || "—"}
        </p>
        <span className="text-xs uppercase tracking-widest border-2 border-black px-4 py-2">{draft.brand_cta || "—"}</span>
      </div>
    ),
  },
  {
    id: "footer",
    title: "Footer",
    fields: [
      { key: "footer_tagline", label: "Large Tagline", hint: 'Giant background text — e.g. "STAY RAD"' },
      { key: "newsletter_heading", label: "Newsletter Heading", hint: 'e.g. "Join The Club"' },
      { key: "newsletter_description", label: "Newsletter Description", hint: "Tagline under the newsletter heading" },
      { key: "newsletter_success", label: "Newsletter Success Message", hint: "Shown after someone subscribes" },
    ],
    Preview: ({ draft }) => (
      <div className="bg-[#0a0a0a] text-white p-4 h-full min-h-[180px]">
        <p className="text-3xl font-black uppercase tracking-tighter text-white/10 mb-4 leading-none">{draft.footer_tagline || "—"}</p>
        <p className="text-sm font-black uppercase tracking-tight mb-1">{draft.newsletter_heading || "—"}</p>
        <p className="text-xs text-gray-400 mb-2">{draft.newsletter_description || "—"}</p>
        <p className="text-xs text-green-400 italic">{draft.newsletter_success || "—"}</p>
      </div>
    ),
  },
];

const IMAGE_FIELDS = [
  { key: "img_hero", label: "Hero — Slide 1", hint: "Main background image behind the BREAK THE RULES headline" },
  { key: "img_hero_2", label: "Hero — Slide 2", hint: "Second slide image (optional — leave empty to disable slider)" },
  { key: "img_hero_3", label: "Hero — Slide 3", hint: "Third slide image (optional)" },
  { key: "img_collection_1", label: "Editorial Panel 1 — Edge", hint: "Large left panel image in the editorial grid" },
  { key: "img_collection_2", label: "Editorial Panel 2 — Street", hint: "Top right panel image in the editorial grid" },
  { key: "img_lookbook", label: "Editorial Panel 3 — Studio", hint: "Bottom right panel image in the editorial grid" },
];

const COLLECTIONS_TEXT_SECTIONS = [
  {
    id: "col_hero",
    title: "Collections Page — Hero",
    fields: [
      { key: "col_hero_eyebrow", label: "Eyebrow", hint: 'e.g. "Season 01"' },
      { key: "col_hero_title", label: "Title", hint: 'e.g. "LOOKBOOK"' },
    ],
    Preview: ({ draft }) => (
      <div className="bg-black text-white p-6 min-h-[100px] flex flex-col justify-end">
        <p className="text-[10px] tracking-widest uppercase opacity-60 mb-1">{draft.col_hero_eyebrow || "—"}</p>
        <p className="text-2xl font-black uppercase tracking-tight">{draft.col_hero_title || "—"}</p>
      </div>
    ),
  },
  {
    id: "col_looks",
    title: "Collections Page — Look Labels",
    fields: [
      { key: "col_look_1_eyebrow", label: "Look 1 — Eyebrow", hint: 'e.g. "Look 01"' },
      { key: "col_look_1_title", label: "Look 1 — Title", hint: 'e.g. "Raw Edge"' },
      { key: "col_look_2_eyebrow", label: "Look 2 — Eyebrow", hint: 'e.g. "Look 02"' },
      { key: "col_look_2_title", label: "Look 2 — Title", hint: 'e.g. "Utility"' },
      { key: "col_look_3_eyebrow", label: "Look 3 — Eyebrow", hint: 'e.g. "Look 03"' },
      { key: "col_look_3_title", label: "Look 3 — Title", hint: 'e.g. "Shadows"' },
      { key: "col_look_4_eyebrow", label: "Look 4 — Eyebrow", hint: 'e.g. "Look 04"' },
      { key: "col_look_4_title", label: "Look 4 — Title", hint: 'e.g. "Golden"' },
      { key: "col_look_5_eyebrow", label: "Look 5 — Eyebrow", hint: 'e.g. "Look 05"' },
      { key: "col_look_5_title", label: "Look 5 — Title", hint: 'e.g. "Concrete"' },
      { key: "col_look_6_eyebrow", label: "Look 6 — Eyebrow", hint: 'e.g. "Look 06"' },
      { key: "col_look_6_title", label: "Look 6 — Title", hint: 'e.g. "Revolt"' },
    ],
    Preview: ({ draft }) => (
      <div className="bg-gray-900 text-white p-4 min-h-[160px] grid grid-cols-3 gap-1">
        {[1,2,3,4,5,6].map(n => (
          <div key={n} className="bg-black/40 p-2 flex flex-col justify-end">
            <p className="text-[9px] tracking-widest uppercase opacity-60">{draft[`col_look_${n}_eyebrow`] || "—"}</p>
            <p className="text-xs font-black uppercase tracking-tight">{draft[`col_look_${n}_title`] || "—"}</p>
          </div>
        ))}
      </div>
    ),
  },
  {
    id: "col_cta",
    title: "Collections Page — CTA",
    fields: [
      { key: "col_cta_eyebrow", label: "Eyebrow", hint: 'e.g. "Shop the look"' },
      { key: "col_cta_title", label: "Heading", hint: 'e.g. "Get The Full Collection"' },
      { key: "col_cta_button", label: "Button Text", hint: 'e.g. "Shop Now"' },
    ],
    Preview: ({ draft }) => (
      <div className="bg-white border border-gray-200 p-6 text-center min-h-[120px] flex flex-col items-center justify-center">
        <p className="text-[10px] tracking-widest uppercase text-gray-400 mb-2">{draft.col_cta_eyebrow || "—"}</p>
        <p className="text-xl font-black uppercase tracking-tight mb-3">{draft.col_cta_title || "—"}</p>
        <span className="text-xs uppercase tracking-widest border-2 border-black px-4 py-2">{draft.col_cta_button || "—"}</span>
      </div>
    ),
  },
];

const COLLECTIONS_IMAGE_FIELDS = [
  { key: "col_hero_img", label: "Collections Hero", hint: "Background image on the Collections page header" },
  { key: "col_look_1_img", label: "Look 1 — Image", hint: "Large left panel (Look 01)" },
  { key: "col_look_2_img", label: "Look 2 — Image", hint: "Top right panel (Look 02)" },
  { key: "col_look_3_img", label: "Look 3 — Image", hint: "Second right panel (Look 03)" },
  { key: "col_look_4_img", label: "Look 4 — Image", hint: "Bottom left panel (Look 04)" },
  { key: "col_look_5_img", label: "Look 5 — Image", hint: "Bottom centre panel (Look 05)" },
  { key: "col_look_6_img", label: "Look 6 — Image", hint: "Bottom right panel (Look 06)" },
];

const ABOUT_TEXT_SECTIONS = [
  {
    id: "about_hero",
    title: "About Page — Hero",
    fields: [
      { key: "about_hero_eyebrow", label: "Eyebrow", hint: 'Small label above the title — e.g. "Est. 2024"' },
      { key: "about_hero_title", label: "Title", hint: 'Main hero headline — e.g. "ABOUT RAD"' },
    ],
    Preview: ({ draft }) => (
      <div className="bg-black text-white p-6 min-h-[100px] flex flex-col justify-end">
        <p className="text-[10px] tracking-widest uppercase opacity-60 mb-1">{draft.about_hero_eyebrow || "—"}</p>
        <p className="text-2xl font-black uppercase tracking-tight">{draft.about_hero_title || "—"}</p>
      </div>
    ),
  },
  {
    id: "about_phil",
    title: "About Page — Philosophy",
    fields: [
      { key: "about_phil_eyebrow", label: "Eyebrow", hint: 'e.g. "Our Philosophy"' },
      { key: "about_phil_title_1", label: "Line 1", hint: 'e.g. "We don\'t follow trends."' },
      { key: "about_phil_title_2", label: "Line 2 (grey)", hint: 'e.g. "We set them on fire."' },
      { key: "about_phil_body_1", label: "Body Paragraph 1", hint: "First paragraph of philosophy text" },
      { key: "about_phil_body_2", label: "Body Paragraph 2", hint: "Second paragraph of philosophy text" },
    ],
    Preview: ({ draft }) => (
      <div className="bg-white border border-gray-200 p-4 min-h-[160px]">
        <p className="text-[10px] tracking-widest uppercase font-bold text-gray-400 mb-2">{draft.about_phil_eyebrow || "—"}</p>
        <p className="text-lg font-black uppercase tracking-tight leading-tight">{draft.about_phil_title_1 || "—"}</p>
        <p className="text-lg font-black uppercase tracking-tight leading-tight text-gray-300 mb-3">{draft.about_phil_title_2 || "—"}</p>
        <p className="text-xs text-gray-500 leading-relaxed mb-1">{draft.about_phil_body_1 || "—"}</p>
        <p className="text-xs text-gray-500 leading-relaxed">{draft.about_phil_body_2 || "—"}</p>
      </div>
    ),
  },
  {
    id: "about_break",
    title: "About Page — Full-Width Break",
    fields: [
      { key: "about_break_text", label: "Overlay Text", hint: 'Giant ghosted word over the image — e.g. "RADICAL"' },
    ],
    Preview: ({ draft }) => (
      <div className="bg-gray-800 p-6 min-h-[80px] flex items-center justify-center">
        <p className="text-3xl font-black uppercase tracking-tighter text-white/20">{draft.about_break_text || "—"}</p>
      </div>
    ),
  },
  {
    id: "about_values",
    title: "About Page — Values",
    fields: [
      { key: "about_values_eyebrow", label: "Section Eyebrow", hint: 'e.g. "What We Stand For"' },
      { key: "about_value_1_title", label: "Value 1 — Title", hint: 'e.g. "Bold Expression"' },
      { key: "about_value_1_body", label: "Value 1 — Description", hint: "Short paragraph for value 1" },
      { key: "about_value_2_title", label: "Value 2 — Title", hint: 'e.g. "Quality Craft"' },
      { key: "about_value_2_body", label: "Value 2 — Description", hint: "Short paragraph for value 2" },
      { key: "about_value_3_title", label: "Value 3 — Title", hint: 'e.g. "Zero Waste"' },
      { key: "about_value_3_body", label: "Value 3 — Description", hint: "Short paragraph for value 3" },
    ],
    Preview: ({ draft }) => (
      <div className="bg-white border border-gray-200 p-4 min-h-[160px]">
        <p className="text-[10px] tracking-widest uppercase font-bold text-gray-400 mb-3">{draft.about_values_eyebrow || "—"}</p>
        <div className="grid grid-cols-3 gap-2">
          {[1, 2, 3].map(n => (
            <div key={n} className="border border-gray-100 p-2">
              <p className="text-xs font-black uppercase tracking-tight mb-1">{draft[`about_value_${n}_title`] || "—"}</p>
              <p className="text-[10px] text-gray-500 leading-relaxed">{draft[`about_value_${n}_body`] || "—"}</p>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    id: "about_quote",
    title: "About Page — Founder's Quote",
    fields: [
      { key: "about_quote_eyebrow", label: "Eyebrow", hint: 'e.g. "Founder\'s Note"' },
      { key: "about_quote_text", label: "Quote", hint: "The main quote text (include quotation marks)" },
      { key: "about_quote_attr", label: "Attribution", hint: 'e.g. "— Creative Director"' },
    ],
    Preview: ({ draft }) => (
      <div className="bg-[#0a0a0a] text-white p-4 min-h-[140px] flex flex-col justify-center">
        <p className="text-[10px] tracking-widest uppercase text-white/40 mb-3">{draft.about_quote_eyebrow || "—"}</p>
        <p className="text-sm font-black uppercase tracking-tight leading-tight mb-3">{draft.about_quote_text || "—"}</p>
        <p className="text-[10px] tracking-widest uppercase text-white/60">{draft.about_quote_attr || "—"}</p>
      </div>
    ),
  },
  {
    id: "about_cta",
    title: "About Page — CTA",
    fields: [
      { key: "about_cta_title", label: "Heading", hint: 'e.g. "Ready to break some rules?"' },
      { key: "about_cta_button", label: "Button Text", hint: 'e.g. "Shop Now"' },
    ],
    Preview: ({ draft }) => (
      <div className="bg-white border border-gray-200 p-6 text-center min-h-[100px] flex flex-col items-center justify-center">
        <p className="text-xl font-black uppercase tracking-tight mb-3">{draft.about_cta_title || "—"}</p>
        <span className="text-xs uppercase tracking-widest border-2 border-black px-4 py-2">{draft.about_cta_button || "—"}</span>
      </div>
    ),
  },
];

const ABOUT_IMAGE_FIELDS = [
  { key: "about_hero_img", label: "About Hero — Background", hint: "Full-bleed image behind the ABOUT RAD headline" },
  { key: "about_break_img", label: "About Mid — Full-Width Break", hint: "Full-width image with the ghosted overlay text (e.g. RADICAL)" },
  { key: "about_quote_img", label: "About Quote — Side Image", hint: "Portrait-style image beside the founder's quote" },
];

const ANNOUNCEMENT_SECTIONS = [
  {
    id: "announcement",
    title: "Announcement Bar",
    fields: [
      { key: "announce_1", label: "Message 1", hint: 'e.g. "FREE SHIPPING OVER $150"' },
      { key: "announce_2", label: "Message 2", hint: 'e.g. "NEW ARRIVALS DROP 01"' },
      { key: "announce_3", label: "Message 3", hint: 'e.g. "JOIN THE RAD CLUB"' },
    ],
    Preview: ({ draft }) => (
      <div className="bg-[#0a0a0a] text-white px-4 py-3 overflow-hidden">
        <div className="flex items-center gap-6 text-[10px] tracking-widest uppercase whitespace-nowrap">
          {[draft.announce_1, draft.announce_2, draft.announce_3].filter(Boolean).map((msg, i) => (
            <React.Fragment key={i}>
              <span>{msg}</span>
              <span className="text-white/40">&bull;</span>
            </React.Fragment>
          ))}
        </div>
      </div>
    ),
  },
];

const SOCIAL_SECTIONS = [
  {
    id: "social",
    title: "Social Media Links",
    fields: [
      { key: "social_instagram", label: "Instagram URL", hint: 'e.g. "https://instagram.com/radwomenswear"' },
      { key: "social_tiktok", label: "TikTok URL", hint: 'e.g. "https://tiktok.com/@radwomenswear"' },
      { key: "social_twitter", label: "X / Twitter URL", hint: 'e.g. "https://x.com/radwomenswear"' },
    ],
    Preview: ({ draft }) => (
      <div className="bg-[#0a0a0a] text-white px-4 py-4 flex gap-4 items-center">
        {draft.social_instagram && <span className="text-xs uppercase tracking-widest text-gray-400 border border-gray-600 px-3 py-1">IG</span>}
        {draft.social_tiktok && <span className="text-xs uppercase tracking-widest text-gray-400 border border-gray-600 px-3 py-1">TikTok</span>}
        {draft.social_twitter && <span className="text-xs uppercase tracking-widest text-gray-400 border border-gray-600 px-3 py-1">X</span>}
        {!draft.social_instagram && !draft.social_tiktok && !draft.social_twitter && (
          <span className="text-xs text-gray-600">No social links set yet</span>
        )}
      </div>
    ),
  },
];

const COPYRIGHT_SECTIONS = [
  {
    id: "copyright",
    title: "Copyright",
    fields: [
      { key: "footer_copyright", label: "Copyright Text", hint: 'Shown at the bottom of every page — e.g. "© 2026 RAD. All rights reserved."' },
    ],
    Preview: ({ draft }) => (
      <div className="bg-[#0a0a0a] text-white p-4 h-full min-h-[80px] flex items-end">
        <p className="text-xs text-gray-500 tracking-wider">{draft.footer_copyright || "© 2026 RAD. All rights reserved."}</p>
      </div>
    ),
  },
];

const CONTACT_TEXT_SECTIONS = [
  {
    id: "contact_header",
    title: "Contact Page — Header",
    fields: [
      { key: "contact_header_eyebrow", label: "Eyebrow", hint: 'Small label above the title — e.g. "Get In Touch"' },
      { key: "contact_header_title", label: "Page Title", hint: 'e.g. "Contact"' },
    ],
    Preview: ({ draft }) => (
      <div className="bg-white border border-gray-200 p-6 min-h-[100px]">
        <p className="text-[10px] tracking-widest uppercase font-bold text-gray-400 mb-2">{draft.contact_header_eyebrow || "—"}</p>
        <p className="text-2xl font-black uppercase tracking-tight">{draft.contact_header_title || "—"}</p>
      </div>
    ),
  },
  {
    id: "contact_info",
    title: "Contact Page — Info Panel",
    fields: [
      { key: "contact_info_heading", label: "Heading", hint: 'e.g. "Let\'s Talk"' },
      { key: "contact_info_body", label: "Body Text", hint: "Paragraph below the heading" },
      { key: "contact_email", label: "Email Address", hint: 'e.g. "hello@rad.com"' },
      { key: "contact_phone", label: "Phone Number", hint: 'e.g. "+1 (555) 123-4567"' },
      { key: "contact_location", label: "Location", hint: 'e.g. "Los Angeles, CA"' },
    ],
    Preview: ({ draft }) => (
      <div className="bg-white border border-gray-200 p-4 min-h-[160px]">
        <p className="text-lg font-black uppercase tracking-tight mb-2">{draft.contact_info_heading || "—"}</p>
        <p className="text-xs text-gray-500 leading-relaxed mb-4">{draft.contact_info_body || "—"}</p>
        <div className="space-y-2">
          <p className="text-xs"><span className="font-bold uppercase tracking-widest text-[10px] mr-2">Email</span>{draft.contact_email || "—"}</p>
          <p className="text-xs"><span className="font-bold uppercase tracking-widest text-[10px] mr-2">Phone</span>{draft.contact_phone || "—"}</p>
          <p className="text-xs"><span className="font-bold uppercase tracking-widest text-[10px] mr-2">Location</span>{draft.contact_location || "—"}</p>
        </div>
      </div>
    ),
  },
  {
    id: "contact_form",
    title: "Contact Page — Form & Messages",
    fields: [
      { key: "contact_submit_btn", label: "Submit Button Text", hint: 'e.g. "Send Message"' },
      { key: "contact_success_heading", label: "Success Heading", hint: 'Shown after form is submitted — e.g. "Message Sent"' },
      { key: "contact_success_body", label: "Success Body Text", hint: "Confirmation message shown after submission" },
      { key: "contact_another_btn", label: "Send Another Button", hint: 'e.g. "Send Another Message"' },
    ],
    Preview: ({ draft }) => (
      <div className="bg-white border border-gray-200 p-4 min-h-[140px] flex flex-col gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-widest font-bold text-gray-400 mb-1">Submit button</p>
          <span className="inline-block text-xs uppercase tracking-widest font-bold border-2 border-black px-4 py-2">{draft.contact_submit_btn || "—"}</span>
        </div>
        <div className="border border-gray-100 p-3 text-center">
          <p className="text-sm font-black uppercase tracking-tight mb-1">{draft.contact_success_heading || "—"}</p>
          <p className="text-xs text-gray-500 mb-2">{draft.contact_success_body || "—"}</p>
          <span className="text-[10px] uppercase tracking-widest border border-black px-3 py-1">{draft.contact_another_btn || "—"}</span>
        </div>
      </div>
    ),
  },
];

function formatKB(bytes) {
  return `${Math.round(bytes / 1024)} KB`;
}

async function compressImage(file, maxWidth = 1800, quality = 0.78) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxWidth / img.width);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        // Only use compressed version if it's actually smaller
        if (blob.size < file.size) {
          resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }));
        } else {
          resolve(file);
        }
      }, 'image/jpeg', quality);
    };
    img.src = url;
  });
}

function ImageField({ label, hint, imgKey, currentUrl, onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [filename, setFilename] = useState(null);
  const [uploadedAt, setUploadedAt] = useState(null);
  const [sizeInfo, setSizeInfo] = useState(null);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    setSizeInfo(null);
    try {
      const originalSize = file.size;
      const compressed = await compressImage(file);
      const compressedSize = compressed.size;
      setSizeInfo({ original: originalSize, compressed: compressedSize });

      const form = new FormData();
      form.append("file", compressed);
      form.append("key", imgKey);
      const token = localStorage.getItem("auth_token");
      const res = await fetch(`${API_URL}/api/site-content/images`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!res.ok) throw new Error("Upload failed");
      const { url } = await res.json();
      setFilename(file.name);
      setUploadedAt(new Date().toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }));
      onUploaded(url);
    } catch {
      setUploadError("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="border border-gray-200 p-4">
      <p className="text-sm font-medium text-gray-700 mb-3">{label}</p>
      <div className="flex gap-4 items-start">
        <div className="w-32 h-20 bg-gray-100 overflow-hidden flex-shrink-0">
          {currentUrl
            ? <img src={currentUrl} alt={label} className="w-full h-full object-cover" />
            : <div className="w-full h-full flex items-center justify-center text-xs text-gray-400">No image</div>
          }
        </div>
        <div className="flex-1">
          <label className="cursor-pointer inline-block">
            <span className={`text-xs uppercase tracking-widest font-bold border border-black px-4 py-2 hover:bg-black hover:text-white transition-colors ${uploading ? "opacity-50 cursor-not-allowed" : ""}`}>
              {uploading ? "Optimising..." : "Replace Image"}
            </span>
            <input type="file" accept="image/*" className="hidden" onChange={handleFile} disabled={uploading} />
          </label>
          {sizeInfo && (
            <div className="mt-2 flex items-center gap-2 text-xs font-mono">
              <span className="text-gray-400 line-through">{formatKB(sizeInfo.original)}</span>
              <span className="text-gray-400">→</span>
              <span className="text-green-600 font-bold">{formatKB(sizeInfo.compressed)}</span>
              <span className="text-green-600">({Math.round((1 - sizeInfo.compressed / sizeInfo.original) * 100)}% smaller)</span>
            </div>
          )}
          {filename && <p className="text-xs text-gray-500 mt-1 font-mono">{filename}</p>}
          {uploadedAt && <p className="text-xs text-gray-400 mt-0.5">Uploaded {uploadedAt}</p>}
          <p className="text-xs text-gray-400 mt-1">{hint}</p>
          {uploadError && <p className="text-xs text-red-500 mt-1">{uploadError}</p>}
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, value, savedValue, onChange, onSave }) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const isDirty = value !== savedValue;

  const handleSave = async () => {
    setSaving(true);
    await onSave();
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="flex gap-2">
        <input
          type="text"
          value={value || ""}
          onChange={e => { onChange(e.target.value); setSaved(false); }}
          className="flex-1 border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
        />
        <button
          onClick={handleSave}
          disabled={!isDirty || saving}
          className={`text-xs uppercase tracking-widest font-bold px-4 border transition-all whitespace-nowrap ${
            saved
              ? 'border-green-500 text-green-600'
              : isDirty && !saving
              ? 'border-black bg-black text-white hover:bg-gray-800'
              : 'border-gray-200 text-gray-300 cursor-not-allowed'
          }`}
        >
          {saving ? '...' : saved ? '✓ Saved' : 'Save'}
        </button>
      </div>
      <p className="text-xs text-gray-400 mt-1">{hint}</p>
    </div>
  );
}

const BRAND_SECTIONS = [
  {
    id: "brand",
    title: "Logo & Brand",
    fields: [
      { key: "logo_text", label: "Logo Text", hint: 'Displayed in the centre of the nav — e.g. "RAD" or "NOVELTY"' },
      { key: "site_title", label: "Browser Tab Title", hint: 'Text shown in the browser tab — e.g. "RAD" or "NOVELTEE"' },
    ],
    Preview: ({ draft }) => (
      <div className="bg-white border border-gray-200 p-6 flex items-center justify-center min-h-[100px]">
        {draft.logo_image ? (
          <img src={draft.logo_image} alt="Logo" className="h-10 w-auto object-contain" />
        ) : (
          <p className="font-black text-4xl tracking-tighter uppercase">{draft.logo_text || "—"}</p>
        )}
      </div>
    ),
  },
];

const BRAND_IMAGE_FIELDS = [
  { key: "logo_image", label: "Logo Image", hint: "Upload a logo image — when set, replaces the text logo. Leave empty to use text." },
];

function makeTabs(baseUrl) {
  return [
    { id: "home",         label: "Home",         url: `${baseUrl}/` },
    { id: "brand",        label: "Brand",        url: `${baseUrl}/` },
    { id: "collections",  label: "Collections",  url: `${baseUrl}/collections` },
    { id: "about",        label: "About",        url: `${baseUrl}/about` },
    { id: "contact",      label: "Contact",      url: `${baseUrl}/contact` },
    { id: "announcement", label: "Announcement", url: `${baseUrl}/` },
    { id: "social",       label: "Social",       url: `${baseUrl}/` },
    { id: "copyright",    label: "Copyright",    url: `${baseUrl}/` },
  ];
}

function SectionGroup({ sections, imageFields, draft, content, handleChange, handleSaveField, showPageMap, prefix = '' }) {
  // For Preview components: strip the prefix so they keep using bare key names
  const previewDraft = prefix
    ? Object.fromEntries(Object.entries(draft).map(([k, v]) => [k.startsWith(prefix) ? k.slice(prefix.length) : k, v]))
    : draft;

  const pk = (key) => prefix + key; // apply prefix to a field key

  return (
    <div className="space-y-12">
      {sections.map(({ id, title, fields, Preview }) => (
        <div key={id}>
          <h2 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4 pb-2 border-b border-gray-100">{title}</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              {fields.map(({ key, label, hint }) => (
                <Field
                  key={pk(key)}
                  label={label}
                  hint={hint}
                  value={draft[pk(key)]}
                  savedValue={content[pk(key)]}
                  onChange={val => handleChange(pk(key), val)}
                  onSave={() => handleSaveField(pk(key), draft[pk(key)])}
                />
              ))}
            </div>
            <div className="lg:sticky lg:top-8 self-start">
              {showPageMap && <PageMap active={id} />}
              <Preview draft={previewDraft} />
            </div>
          </div>
        </div>
      ))}
      {imageFields && imageFields.length > 0 && (
        <div>
          <h2 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4 pb-2 border-b border-gray-100">Images</h2>
          <div className="space-y-4">
            {imageFields.map(({ key, label, hint }) => (
              <ImageField
                key={pk(key)}
                label={label}
                hint={hint}
                imgKey={pk(key)}
                currentUrl={draft[pk(key)] || ""}
                onUploaded={(url) => handleChange(pk(key), url)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function StoreEditor({ prefix = '', storeLabel = 'RAD Store', storeBaseUrl = 'https://rad-one.pages.dev' }) {
  const TABS = makeTabs(storeBaseUrl);
  const [content, setContent] = useState({});
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("home");

  useEffect(() => {
    fetch(`${API_URL}/api/site-content`)
      .then(r => r.json())
      .then(data => { setContent(data); setDraft(data); })
      .catch(() => setError("Failed to load content"))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (key, value) => {
    setDraft(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveField = async (key, value) => {
    const token = localStorage.getItem("auth_token");
    const res = await fetch(`${API_URL}/api/site-content`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      credentials: "include",
      body: JSON.stringify({ [key]: value }),
    });
    if (res.ok) setContent(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4 max-w-2xl">
          {[1, 2, 3, 4].map(i => (
            <div key={i}>
              <div className="h-4 bg-gray-200 rounded w-32 mb-2" />
              <div className="h-10 bg-gray-200 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const activeTabConfig = TABS.find(t => t.id === activeTab);

  return (
    <div>
      {/* Page header */}
      <div className="px-8 pt-8 pb-0">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{storeLabel}</h1>
            <p className="text-sm text-gray-500 mt-1">Edit text and images on the {storeLabel} storefront.</p>
          </div>
          <a
            href={activeTabConfig.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs uppercase tracking-widest font-bold border border-gray-300 px-4 py-2 text-gray-500 hover:border-black hover:text-black transition-colors"
          >
            View {activeTabConfig.label} on {storeLabel} →
          </a>
        </div>

        {/* Tab bar */}
        <div className="flex border-b border-gray-200">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 text-sm font-medium tracking-wide border-b-2 transition-colors -mb-px ${
                activeTab === tab.id
                  ? "border-black text-black"
                  : "border-transparent text-gray-400 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="px-8 py-8">
        {error && <p className="text-sm text-red-500 mb-6">{error}</p>}

        {activeTab === "home" && (
          <SectionGroup
            sections={SECTIONS}
            imageFields={IMAGE_FIELDS}
            draft={draft}
            content={content}
            handleChange={handleChange}
            handleSaveField={handleSaveField}
            showPageMap={true}
            prefix={prefix}
          />
        )}

        {activeTab === "brand" && (
          <SectionGroup
            sections={BRAND_SECTIONS}
            imageFields={BRAND_IMAGE_FIELDS}
            draft={draft}
            content={content}
            handleChange={handleChange}
            handleSaveField={handleSaveField}
            showPageMap={false}
            prefix={prefix}
          />
        )}

        {activeTab === "collections" && (
          <SectionGroup
            sections={COLLECTIONS_TEXT_SECTIONS}
            imageFields={COLLECTIONS_IMAGE_FIELDS}
            draft={draft}
            content={content}
            handleChange={handleChange}
            handleSaveField={handleSaveField}
            showPageMap={false}
            prefix={prefix}
          />
        )}

        {activeTab === "about" && (
          <SectionGroup
            sections={ABOUT_TEXT_SECTIONS}
            imageFields={ABOUT_IMAGE_FIELDS}
            draft={draft}
            content={content}
            handleChange={handleChange}
            handleSaveField={handleSaveField}
            showPageMap={false}
            prefix={prefix}
          />
        )}

        {activeTab === "contact" && (
          <SectionGroup
            sections={CONTACT_TEXT_SECTIONS}
            imageFields={[]}
            draft={draft}
            content={content}
            handleChange={handleChange}
            handleSaveField={handleSaveField}
            showPageMap={false}
            prefix={prefix}
          />
        )}

        {activeTab === "announcement" && (
          <SectionGroup
            sections={ANNOUNCEMENT_SECTIONS}
            imageFields={[]}
            draft={draft}
            content={content}
            handleChange={handleChange}
            handleSaveField={handleSaveField}
            showPageMap={false}
            prefix={prefix}
          />
        )}

        {activeTab === "social" && (
          <SectionGroup
            sections={SOCIAL_SECTIONS}
            imageFields={[]}
            draft={draft}
            content={content}
            handleChange={handleChange}
            handleSaveField={handleSaveField}
            showPageMap={false}
            prefix={prefix}
          />
        )}

        {activeTab === "copyright" && (
          <SectionGroup
            sections={COPYRIGHT_SECTIONS}
            imageFields={[]}
            draft={draft}
            content={content}
            handleChange={handleChange}
            handleSaveField={handleSaveField}
            showPageMap={false}
            prefix={prefix}
          />
        )}
      </div>
    </div>
  );
}

export default function RadStore() {
  return <StoreEditor />;
}
